import React, { useEffect, useState } from "react";
import { Label, Input, Dropdown, Option, Button } from "@fluentui/react-components";
import { Eye20Regular, EyeOff20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import InfoButton from "../GenericComponent/InfoButton";
import TimeZoneSelector from "../GenericComponent/TimeZone";
import Language from "../GenericComponent/Language";
import "../CSS/ChatFlowsComponent.css";

export default function ResourcesAccountChatComponent({
  chatFlowData,
  setChatFlowData,
  isEditMode,
  permissions,
  st_CallFlows,
  SCOPES,
  isInDiagramPanel = false,
  isWhatsAppRoute = false
}) {
  const { t } = useTranslation();
  
  // Récupérer les scopes depuis les props du container
  const scopes = Array.isArray(SCOPES) ? SCOPES : (SCOPES ? Object.keys(SCOPES) : []);

  const [isWhatsAppFlow, setIsWhatsAppFlow] = useState(isWhatsAppRoute);
  const [showToken, setShowToken] = useState(false);

  const [formValues, setFormValues] = useState({
    id: "",
    botId: "",
    displayName: "",
    botKey: "",
    attachedFlowId: "",
    attachedFlowDisplayName: "",
    scope: "",
    timeZone: "",
    language: "en-US",
    region: "",
    dataLocation: "",
    isTPE: false,
    // WhatsApp specific fields
    whatsAppId: "",
    teamsBotId: "",
    token: "",
  });

  // Detect WhatsApp flow
  useEffect(() => {
    if (isEditMode && (chatFlowData?.whatsAppId || chatFlowData?.teamsBotId)) {
      setIsWhatsAppFlow(true);
    } else if (!isEditMode) {
      setIsWhatsAppFlow(isWhatsAppRoute);
    }
  }, [chatFlowData, isEditMode, isWhatsAppRoute]);

  // Populate form values from chatFlowData
  useEffect(() => {
    if (chatFlowData) {
      // Utiliser le premier scope disponible comme fallback
      const defaultScope = scopes.length > 0 ? scopes[0] : "";
      const defaultLanguage = chatFlowData.language || "en-US";
      const defaultTimeZone = chatFlowData.timeZone || "Europe/Paris";
      
      setFormValues({
        id: chatFlowData.id || "",
        botId: chatFlowData.botId || chatFlowData.teamsBotId || "",
        displayName: chatFlowData.displayName || "",
        botKey: chatFlowData.password || chatFlowData.botKey || "",
        attachedFlowId: chatFlowData.attachedFlowId?.id || "",
        attachedFlowDisplayName: chatFlowData.attachedFlowId?.displayName || "",
        scope: String(chatFlowData.scope || defaultScope),
        timeZone: defaultTimeZone,
        language: defaultLanguage,
        region: String(chatFlowData.region || ""),
        dataLocation: String(chatFlowData.dataLocation?.id || chatFlowData.dataLocation || ""),
        isTPE: !!chatFlowData.isTPE,
        // WhatsApp specific fields
        whatsAppId: chatFlowData.whatsAppId || "",
        teamsBotId: chatFlowData.teamsBotId || "",
        token: chatFlowData.token || "",
      });
      
      // Mettre à jour chatFlowData avec les valeurs par défaut si elles sont manquantes
      if (!chatFlowData.language || !chatFlowData.timeZone) {
        setChatFlowData(prev => ({
          ...prev,
          language: defaultLanguage,
          timeZone: defaultTimeZone
        }));
      }
    }
  }, [chatFlowData, scopes]);

  // Auto-select first CallFlow when creating
  useEffect(() => {
    if (!isEditMode && st_CallFlows?.length > 0) {
      const hasNoAttachedFlow = !chatFlowData.attachedFlowId?.id || chatFlowData.attachedFlowId.id === "";
      if (hasNoAttachedFlow && !formValues.attachedFlowId) {
        const firstFlow = st_CallFlows[0];
        setChatFlowData(prev => ({
          ...prev,
          attachedFlowId: {
            id: firstFlow.id,
            displayName: firstFlow.displayName
          }
        }));
        setFormValues(prev => ({
          ...prev,
          attachedFlowId: firstFlow.id,
          attachedFlowDisplayName: firstFlow.displayName
        }));
      }
    }
  }, [st_CallFlows, isEditMode, chatFlowData.attachedFlowId?.id]);

  const updateField = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setChatFlowData(prev => ({ ...prev, [field]: value }));
  };

  const handleAttachedFlowChange = (flowId) => {
    const selectedFlow = st_CallFlows?.find(f => f.id === flowId);
    if (selectedFlow) {
      setChatFlowData(prev => ({
        ...prev,
        attachedFlowId: { id: selectedFlow.id, displayName: selectedFlow.displayName }
      }));
      setFormValues(prev => ({
        ...prev,
        attachedFlowId: selectedFlow.id,
        attachedFlowDisplayName: selectedFlow.displayName
      }));
    }
  };

  const canEdit = isEditMode
    ? permissions?.resourceAccount?.canUpdate
    : permissions?.resourceAccount?.canCreate;

  return (
    <div className="chatflow-resources-account-form">
      <div className="resources-account-content">
        {/* Edit Mode */}
        {isEditMode && (
          <>
            {/* Single column list layout for diagram panel */}
            {isInDiagramPanel ? (
              <>
                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label">
                    {t("SCOPE")}
                    <InfoButton infoKey="scope" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    value={formValues.scope}
                    disabled
                    style={{ backgroundColor: "#f5f5f5" }}
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("DISPLAY_NAME")}
                    <InfoButton infoKey="displayName" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    value={formValues.displayName}
                    onChange={(e) => updateField("displayName", e.target.value)}
                    disabled={!canEdit}
                    required
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("TIMEZONE")}
                    <InfoButton infoKey="timeZone" category="CHAT_FLOWS" />
                  </Label>
                  <TimeZoneSelector
                    value={formValues.timeZone}
                    onSelect={(value) => updateField("timeZone", value)}
                    disabled={!canEdit}
                    isUpdate={false}
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("LANGUAGE")}
                    <InfoButton infoKey="language" category="CHAT_FLOWS" />
                  </Label>
                  <Language
                    language={formValues.language}
                    setLangue={(value) => updateField("language", value)}
                    disabled={!canEdit}
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("BOT_KEY")}
                    <InfoButton infoKey="botKey" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    type="password"
                    value={formValues.botKey}
                    onChange={(e) => updateField("botKey", e.target.value)}
                    required
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label">
                    {t("CALL_FLOW_LINK")}
                    <InfoButton infoKey="callFlowLink" category="CHAT_FLOWS" />
                  </Label>
                  <Dropdown
                    className="chatflow-dropdown"
                    value={formValues.attachedFlowDisplayName}
                    selectedOptions={[formValues.attachedFlowId]}
                    onOptionSelect={(_, data) => handleAttachedFlowChange(data.optionValue)}
                    disabled={!st_CallFlows || st_CallFlows.length === 0}
                    placeholder={st_CallFlows?.length === 0 ? t("NO_CALL_FLOWS_AVAILABLE") : t("SELECT_CALL_FLOW")}
                  >
                    {st_CallFlows?.map((flow) => (
                      <Option key={flow.id} value={flow.id}>
                        {flow.displayName}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label">
                    {t("CHAT_FLOW_ID")}
                    <InfoButton infoKey="chatFlowId" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    value={formValues.id}
                    disabled
                    style={{ backgroundColor: "#f5f5f5" }}
                  />
                </div>
              </>
            ) : (
              /* Two column layout for normal view */
              <div className="chatflow-form-row">
                {/* Left column */}
                <div className="chatflow-form-column">
                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label" required>
                      {t("DISPLAY_NAME")}
                      <InfoButton infoKey="displayName" category="CHAT_FLOWS" />
                    </Label>
                    <Input
                      className="chatflow-input"
                      value={formValues.displayName}
                      onChange={(e) => updateField("displayName", e.target.value)}
                      disabled={!canEdit}
                      required
                    />
                  </div>

                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label" required>
                      {t("TIMEZONE")}
                      <InfoButton infoKey="timeZone" category="CHAT_FLOWS" />
                    </Label>
                    <TimeZoneSelector
                      value={formValues.timeZone}
                      onSelect={(value) => updateField("timeZone", value)}
                      disabled={!canEdit}
                      isUpdate={false}
                    />
                  </div>

                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label" required>
                      {t("LANGUAGE")}
                      <InfoButton infoKey="language" category="CHAT_FLOWS" />
                    </Label>
                    <Language
                      language={formValues.language}
                      setLangue={(value) => updateField("language", value)}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label">
                      {t("CHAT_FLOW_ID")}
                      <InfoButton infoKey="chatFlowId" category="CHAT_FLOWS" />
                    </Label>
                    <Input
                      className="chatflow-input"
                      value={formValues.id}
                      disabled
                      style={{ backgroundColor: "#f5f5f5" }}
                    />
                  </div>
                  {isWhatsAppFlow && (
                    <div className="chatflow-form-column">
                      <div className="chatflow-field-wrapper">
                        <Label className="chatflow-label" required>
                          WhatsApp ID
                          <InfoButton infoKey="whatsAppId" category="CHAT_FLOWS" />
                        </Label>
                        <Input
                          className="chatflow-input"
                          value={formValues.whatsAppId}
                          onChange={(e) => updateField("whatsAppId", e.target.value)}
                          disabled={!canEdit}
                          required
                        />
                      </div>
                    </div>)}
                </div>

                {/* Right column */}
                <div className="chatflow-form-column">
                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label">
                      {t("SCOPE")}
                      <InfoButton infoKey="scope" category="CHAT_FLOWS" />
                    </Label>
                    <Input
                      className="chatflow-input"
                      value={formValues.scope}
                      disabled
                      style={{ backgroundColor: "#f5f5f5" }}
                    />
                  </div>

                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label" required>
                      {t("BOT_KEY")}
                      <InfoButton infoKey="botKey" category="CHAT_FLOWS" />
                    </Label>
                    <Input
                      className="chatflow-input"
                      type="password"
                      value={formValues.botKey}
                      onChange={(e) => updateField("botKey", e.target.value)}
                      required
                    />
                  </div>

                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label">
                      {t("CALL_FLOW_LINK")}
                      <InfoButton infoKey="callFlowLink" category="CHAT_FLOWS" />
                    </Label>
                    <Dropdown
                      className="chatflow-dropdown"
                      value={formValues.attachedFlowDisplayName}
                      selectedOptions={[formValues.attachedFlowId]}
                      onOptionSelect={(_, data) => handleAttachedFlowChange(data.optionValue)}
                      disabled={!st_CallFlows || st_CallFlows.length === 0}
                      placeholder={st_CallFlows?.length === 0 ? t("NO_CALL_FLOWS_AVAILABLE") : t("SELECT_CALL_FLOW")}
                    >
                      {st_CallFlows?.map((flow) => (
                        <Option key={flow.id} value={flow.id}>
                          {flow.displayName}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>
                  {isWhatsAppFlow && (
                    <>


                      <div className="chatflow-form-column">


                        <div className="chatflow-field-wrapper">
                          <Label className="chatflow-label" required>
                            WhatsApp Token
                            <InfoButton infoKey="token" category="CHAT_FLOWS" />
                          </Label>
                          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <Input
                              className="chatflow-input"
                              type={showToken ? "text" : "password"}
                              value={formValues.token || ""}
                              onChange={(e) => updateField("token", e.target.value)}
                              disabled={!canEdit}
                              required
                              placeholder="••••••••••••••••"
                              style={{ width: "100%" }}
                            />
                            <Button
                              appearance="subtle"
                              icon={showToken ? <EyeOff20Regular /> : <Eye20Regular />}
                              onClick={() => setShowToken(!showToken)}
                              title={showToken ? "Masquer" : "Afficher"}
                              style={{ minWidth: "auto" }}
                            />
                          </div>
                        </div>
                      </div>
                    </>)}
                </div>
              </div>
            )}

            {/* WhatsApp specific fields in edit mode */}
            {isWhatsAppFlow && (
              <>
                {isInDiagramPanel && (
                  /* Single column for diagram panel */
                  <>
                    <div className="chatflow-field-wrapper">
                      <Label className="chatflow-label" required>
                        WhatsApp ID
                        <InfoButton infoKey="whatsAppId" category="CHAT_FLOWS" />
                      </Label>
                      <Input
                        className="chatflow-input"
                        value={formValues.whatsAppId}
                        onChange={(e) => updateField("whatsAppId", e.target.value)}
                        disabled={!canEdit}
                        required
                      />
                    </div>

                    <div className="chatflow-field-wrapper">
                      <Label className="chatflow-label" required>
                        Teams Bot ID
                        <InfoButton infoKey="teamsBotId" category="CHAT_FLOWS" />
                      </Label>
                      <Input
                        className="chatflow-input"
                        value={formValues.teamsBotId}
                        onChange={(e) => updateField("teamsBotId", e.target.value)}
                        disabled={!canEdit}
                        required
                      />
                    </div>

                    <div className="chatflow-field-wrapper">
                      <Label className="chatflow-label" required>
                        WhatsApp Token
                        <InfoButton infoKey="token" category="CHAT_FLOWS" />
                      </Label>
                      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Input
                          className="chatflow-input"
                          type={showToken ? "text" : "password"}
                          value={formValues.token || ""}
                          onChange={(e) => updateField("token", e.target.value)}
                          disabled={!canEdit}
                          required
                          placeholder="••••••••••••••••"
                          style={{ width: "100%" }}
                        />
                        <Button
                          appearance="subtle"
                          icon={showToken ? <EyeOff20Regular /> : <Eye20Regular />}
                          onClick={() => setShowToken(!showToken)}
                          title={showToken ? "Masquer" : "Afficher"}
                          style={{ minWidth: "auto" }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Create Mode */}
        {!isEditMode && (
          <>
            <div className="chatflow-form-row">
              <div className="chatflow-form-column">
                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("DISPLAY_NAME")}
                    <InfoButton infoKey="displayName" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    value={formValues.displayName}
                    onChange={(e) => updateField("displayName", e.target.value)}
                    disabled={!canEdit}
                    required
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("BOT_KEY")}
                    <InfoButton infoKey="botKey" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    type="password"
                    value={formValues.botKey}
                    onChange={(e) => updateField("botKey", e.target.value)}
                    disabled={!canEdit}
                    required
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("ATTACHED_FLOW")}
                    <InfoButton infoKey="attachedFlow" category="CHAT_FLOWS" />
                  </Label>
                  <Dropdown
                    className="chatflow-dropdown"
                    value={formValues.attachedFlowDisplayName}
                    selectedOptions={[formValues.attachedFlowId]}
                    onOptionSelect={(_, data) => handleAttachedFlowChange(data.optionValue)}
                    disabled={!canEdit || !st_CallFlows || st_CallFlows.length === 0}
                    placeholder={st_CallFlows?.length === 0 ? t("NO_CALL_FLOWS_AVAILABLE") : t("SELECT_CALL_FLOW")}
                  >
                    {st_CallFlows?.map(flow => (
                      <Option key={flow.id} value={flow.id}>
                        {flow.displayName}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              </div>

              <div className="chatflow-form-column">
                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {isWhatsAppFlow ? "Teams Bot ID" : t("BOT_ID")}
                    <InfoButton infoKey="botId" category="CHAT_FLOWS" />
                  </Label>
                  <Input
                    className="chatflow-input"
                    value={formValues.botId}
                    onChange={(e) => {
                      updateField("botId", e.target.value);
                      if (isWhatsAppFlow) updateField("teamsBotId", e.target.value);
                    }}
                    disabled={!canEdit}
                    required
                  />
                </div>

                <div className="chatflow-field-wrapper">
                  <Label className="chatflow-label" required>
                    {t("SCOPE")}
                    <InfoButton infoKey="scope" category="CHAT_FLOWS" />
                  </Label>
                  <Dropdown
                    className="chatflow-dropdown"
                    value={formValues.scope}
                    selectedOptions={[formValues.scope]}
                    onOptionSelect={(_, data) => updateField("scope", data.optionValue)}
                    disabled={!canEdit}
                  >
                    {scopes.map((scope) => (
                      <Option key={scope} value={scope}>
                        {scope}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
                {isWhatsAppFlow && (
                  <div className="chatflow-form-column">
                    <div className="chatflow-field-wrapper">
                      <Label className="chatflow-label" required>
                        WhatsApp Token
                        <InfoButton infoKey="token" category="CHAT_FLOWS" />
                      </Label>
                      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Input
                          className="chatflow-input"
                          type={showToken ? "text" : "password"}
                          value={formValues.token || ""}
                          onChange={(e) => updateField("token", e.target.value)}
                          disabled={!canEdit}
                          required
                          placeholder="••••••••••••••••"
                          style={{ flex: 1 }}
                        />
                        <Button
                          appearance="subtle"
                          icon={showToken ? <EyeOff20Regular /> : <Eye20Regular />}
                          onClick={() => setShowToken(!showToken)}
                          title={showToken ? "Masquer" : "Afficher"}
                          style={{ minWidth: "auto" }}
                        />
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* WhatsApp fields in create mode */}
            {isWhatsAppFlow && (
              <div className="chatflow-form-row">
                <div className="chatflow-form-column">
                  <div className="chatflow-field-wrapper">
                    <Label className="chatflow-label" required>
                      WhatsApp ID
                      <InfoButton infoKey="whatsAppId" category="CHAT_FLOWS" />
                    </Label>
                    <Input
                      className="chatflow-input"
                      value={formValues.whatsAppId}
                      onChange={(e) => updateField("whatsAppId", e.target.value)}
                      disabled={!canEdit}
                      required
                    />
                  </div>
                </div>

                <div className="chatflow-form-column">

                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
